import type { CapiPlp } from 'types/capi/plp';
import type { User } from 'types/User';

import gtm from '~/services/gtm.client';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import {
  getEntrypoint,
  getPreviousPagePath,
  getPreviousPageType,
  getSourceAndMedium
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp?: string;
  isBot: boolean;
  listId: string;
  plp: CapiPlp;
  user: User;
};

class PlpViewEvent extends UserEvent<EventData> {
  private getProductIds() {
    return this.data.plp.products.map((product) => product.id);
  }

  private getNavCtxPayload() {
    return {
      entry_point: getEntrypoint(),
      list_id: this.data.listId,
      page_path: window.location.pathname,
      page_type: 'pdp' as const,
      platform: 'web' as const,
      position: undefined,
      previous_page_path: getPreviousPagePath(),
      previous_page_type: getPreviousPageType(),
      source_medium: getSourceAndMedium(),
      url: window.location.href
    };
  }

  sendToTikTok() {
    return tiktok.fireEvent('ViewContent', {
      content_ids: this.getProductIds(),
      content_type: 'product'
    });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: this.getProductIds().map((productId) => ({
        content_id: productId,
        content_type: 'product'
      }))
    });
  }

  sendToGtm() {
    return gtm.Ecommerce.viewListItems(
      this.data.plp.products,
      window.location.pathname + window.location.search,
      this.getNavCtxPayload()
    );
  }

  collectServerEvents() {
    const { plp, clientIp } = this.data;
    const url = window.location.href;
    const urlObj = new URL(url);

    const serverEventData = {
      client_ip: clientIp,
      currency: plp.products[0]?.price.currency,
      has_search_term: urlObj.searchParams.has('q'),
      is_filtered: plp.filter_options.some((o) => o.active),
      is_logged_in: this.data.user.isLoggedIn,
      is_sorted: urlObj.searchParams.has('sort'),
      item_list_name: urlObj.pathname + urlObj.search,
      items: this.getListItems(),
      product_type: plp.type,
      useragent: navigator.userAgent,
      ...this.getNavCtxPayload()
    };

    this.serverEvents.push({ data: serverEventData, type: 'plp_view' });
  }

  private getListItems() {
    return this.data.plp.products.map((product, i) => {
      return {
        index: i + 1,
        item_brand: product.brand, // this was coming through anyway
        item_category: product.genders?.join(', '), // same as above
        item_category2: product.sub_type, //same as above
        item_id: product.style_code,
        item_name: product.name,
        item_variant: product.model,
        location_id: product.stock_status,
        price: product.price.sale ?? product.price.regular ?? undefined
      };
    });
  }
}

export default PlpViewEvent;
